"use client";

import { useTablePage } from "@/lib/providers";
import { useStatCardFilters, useTransitioningValue, type StatFilterPreset } from "@/lib/hooks";
import { StatCardButton, type StatCardColor } from "./StatCard";
import { cn } from "@/lib/utils";

export interface StatCardFilterItem extends StatFilterPreset {
  label: string;
  value: number | null;
  color?: StatCardColor;
  views?: Array<"standard" | "archived">;
}

interface StatCardFiltersProps {
  cards: StatCardFilterItem[];
  className?: string;
  /**
   * When `true`, clicking a card keeps any unrelated active filter chips and
   * only swaps the status dimension the cards own. Defaults to `false`
   * (clicking a card replaces the whole filter set).
   */
  preserveOtherFilters?: boolean;
}

export function StatCardFilters({
  cards,
  className,
  preserveOtherFilters = false,
}: StatCardFiltersProps) {
  const { searchParams, currentFilters, handleFiltersChange, isPending } = useTablePage();

  const presets: StatFilterPreset[] = cards.map(function toPreset(card) {
    return { key: card.key, filter: card.filter };
  });

  const { activePresetKey, onStatCardClick } = useStatCardFilters({
    filters: currentFilters,
    presets,
    onFiltersChange: handleFiltersChange,
    preserveOtherFilters,
  });

  const showArchived = searchParams.get("show_archived") === "true";
  const isViewChanging = useTransitioningValue(showArchived, isPending);
  const currentView = showArchived ? "archived" : "standard";

  const visibleCards = cards.filter(function isVisibleInView(card) {
    const cardViews = card.views ?? ["standard", "archived"];
    return cardViews.includes(currentView);
  });

  if (visibleCards.length === 0) return null;

  return (
    <div
      className={cn(
        "grid gap-3 mb-4 grid-cols-[repeat(auto-fit,minmax(160px,1fr))]",
        className,
      )}
    >
      {visibleCards.map(function renderCard(card) {
        const displayValue =
          card.value === null
            ? "count unavailable"
            : card.value.toLocaleString("en-US");

        return (
          <StatCardButton
            key={card.key}
            color={card.color}
            label={card.label}
            value={displayValue}
            loading={isViewChanging}
            active={!isViewChanging && activePresetKey === card.key}
            aria-label={`${card.label}: ${isViewChanging ? "loading" : displayValue}`}
            onClick={function handleClick() {
              onStatCardClick(card.key);
            }}
          />
        );
      })}
    </div>
  );
}
